"use client";
import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    _retry?: boolean;
  }
}

interface ApiConfig {
  baseURL: string;
  timeout: number;
  headers: object;
  withCredentials: boolean;
}

interface ErrorResponse {
  status?: number;
  message?: string;
  data?: any;
}
console.log(process.env)
const apiConfig: ApiConfig = {
  baseURL: process.env.NEXT_PUBLIC_APP_URL || "https://dev-api.travelfirst.in/api/v1",
  timeout: Number(process.env.API_TIMEOUT) || 100000000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false
};

const axiosInstance: AxiosInstance = axios.create(apiConfig);

// Add a request interceptor to dynamically set the Authorization header
axiosInstance.interceptors.request.use(
  (config) => {
    // Initialize headers if they don't exist
    config.headers = config.headers || {};

    const token = localStorage.getItem("access_token");
    console.log("Request Interceptor - Token:", token); // Debug log

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Define the structure of a retry queue item
interface RetryQueueItem {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
}

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

// Add this function outside the interceptor
const handleLogout = async (isSessionTimeout: boolean = false) => {
  console.log("Handling logout");
  const currentPath = window.location.pathname;

  // Only clear token and redirect if not already on login page
  if (!currentPath.includes('/')) {
    // Clear the token
    localStorage.removeItem('access_token');
    // Set a flag in sessionStorage to indicate session expiration

    if (isSessionTimeout) {
      // This is more reliable than events for cross-component communication
      sessionStorage.setItem('session_expired', 'true');

      // Use a more reliable approach than custom events
      // Simple redirect with a short delay to allow React to clean up
      setTimeout(() => {
        window.location.href = '/';
      }, 300);
    }else{
      setTimeout(() => {
        window.location.href = '/login';
      }, 300);
    }

  }
};

// Error handling interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log("Response Interceptor - Success:", response.status,"1111"); // Debug log
    return response;
  },
  async (error: any) => {
    const originalRequest: any = error.config;
    const { response } = error;

    console.log("Response Interceptor - Error:", { // Debug log
      status: response?.status,
      isRetry: originalRequest._retry,
      isRefreshing
    });

    // Don't retry if this is already a retry attempt
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // Only attempt token refresh if it's a 401 error
    if (response?.status === 401) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        console.log("Starting token refresh"); // Debug log

        try {
          const currentToken = localStorage.getItem('access_token');
          console.log("Current token before refresh:", currentToken); // Debug log

          const refreshResponse = await axiosInstance.post('/auth/refresh', {}, {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${currentToken}`
            },
            _retry: true
          });

          const { access_token } = refreshResponse.data;

          if (!access_token) {
            throw new Error('No access token received');
          }

          console.log("New token received:", access_token); // Debug log

          // Update token in localStorage and axios headers
          localStorage.setItem('access_token', access_token);
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          // Process queue and clear it
          refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
            config.headers = config.headers || {};
            config.headers['Authorization'] = `Bearer ${access_token}`;
            axiosInstance
              .request(config)
              .then(resolve)
              .catch(reject);
          });
          refreshAndRetryQueue.length = 0;

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // Only logout if the refresh token is invalid or expired
          if ((refreshError as AxiosError)?.response?.status === 401) {
            await handleLogout();
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Add request to queue if we're already refreshing
      return new Promise((resolve, reject) => {
        refreshAndRetryQueue.push({
          config: originalRequest,
          resolve,
          reject
        });
      });
    }
    if (response?.status === 440) {
      handleLogout(true)
    }

    return Promise.reject(error);
  }
);

// API methods
const api = {
  // GET request
  get: <T>(url: string, params?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    axiosInstance.get<T>(url, { params, ...config }),

  // POST request
  post: <T>(url: string, data: any, config?: { withCredentials?: boolean; headers?: object }): Promise<AxiosResponse<T>> =>
    axiosInstance.post<T>(url, data, config),

  // PUT request
  put: <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    axiosInstance.put<T>(url, data, config),

  // PATCH REQUEST
  patch: <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    axiosInstance.patch<T>(url, data, config),

  // DELETE request
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    axiosInstance.delete<T>(url, config),
};

export { axiosInstance };
export default api;